import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import type { ComponentProps } from 'react';
import { createContext, useContext, useId, useLayoutEffect, useRef, useState } from 'react';
import { mergeStyling } from '../common-functions';

const PopoverIdContext = createContext<string | null>(null);

const Popover = ({ ...props }: PopoverPrimitive.Root.Props) => {
  const id = useId();
  return (
    <PopoverIdContext value={id}>
      <PopoverPrimitive.Root data-slot="popover" {...props} />
    </PopoverIdContext>
  );
};

const PopoverTrigger = ({ className, ...props }: PopoverPrimitive.Trigger.Props) => {
  const popoverId = useContext(PopoverIdContext);
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      data-popover-id={popoverId}
      className={mergeStyling('outline-hidden focus-visible:outline-2 focus-visible:outline-primary-1', className)}
      {...props}
    />
  );
};

const PopoverClose = ({ ...props }: PopoverPrimitive.Close.Props) => {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
};

type PopoverAlign = PopoverPrimitive.Positioner.Props['align'];
type PopoverSide = PopoverPrimitive.Positioner.Props['side'];

const PopoverContent = ({
  className,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  collisionAvoidance,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<PopoverPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset' | 'collisionAvoidance'>) => {
  const popoverId = useContext(PopoverIdContext);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | undefined>(undefined);
  const detectionDoneRef = useRef(false);

  // Auto-detect parent dialog using the unique popover ID to find the correct trigger.
  // We render multiple hidden dialogs at once, so we need to ensure we target the correct dialog.
  useLayoutEffect(() => {
    if (detectionDoneRef.current || !popoverId) return;

    detectionDoneRef.current = true;

    const triggerElement: HTMLElement | null = document.querySelector(
      `[data-slot="popover-trigger"][data-popover-id="${popoverId}"]`
    );

    if (!triggerElement) return;

    const dialogAncestor = triggerElement.closest('dialog');
    if (dialogAncestor) {
      setPortalContainer(dialogAncestor);
    } else {
      setPortalContainer(undefined);
    }
  }, [popoverId]);

  return (
    <PopoverPrimitive.Portal container={portalContainer}>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        collisionAvoidance={collisionAvoidance}
        className="isolate z-50"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={mergeStyling(
            'flex flex-col gap-2.5 p-2.5 text-sm rounded-sm shadow-md bg-mono-1 text-mono-0 z-50 w-72 ring-gray-4 ring-1 duration-100 outline-hidden',
            className
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
};

const PopoverHeader = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div data-slot="popover-header" className={mergeStyling('flex flex-col gap-1 text-sm', className)} {...props} />
  );
};

const PopoverTitle = ({ className, ...props }: PopoverPrimitive.Title.Props) => {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={mergeStyling('font-medium text-base', className)}
      {...props}
    />
  );
};

const PopoverDescription = ({ className, ...props }: PopoverPrimitive.Description.Props) => {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={mergeStyling('text-gray-2', className)}
      {...props}
    />
  );
};

export {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  type PopoverAlign,
  type PopoverSide,
};
